import { type ClipboardEvent, type ReactElement, useEffect, useRef } from 'react';

/**
 * The one-time-code field of every Vello web system (V-LIB-SHELL, D509; audit F23 + admin #3): ONE real `<input>`,
 * transparent, laid over `length` drawn cells. Because the input is the real thing — not a decoration behind a click
 * handler — the browser, the screen reader and the OS code autofill all talk to it (`autocomplete="one-time-code"`,
 * numeric keypad), and a tap anywhere on the cells lands on it.
 *
 * Focus (design standard §4, D424): ONE ring, never a double one. The input draws none itself (it covers the whole
 * row); the ACTIVE cell takes the single halo + the 2px ink outline drawn flush inside it. The active cell is the next
 * empty one while typing and the LAST cell once the code is full, so a full code still shows where focus is. The ring is
 * on `:focus-within`, so it is shown for pointer and keyboard alike (a text field holds the caret either way).
 *
 * Input is normalised: anything that is not a digit is dropped and the value is cut to `length`, for typing, the
 * keyboard's one-time-code suggestion and paste ("123 456", "123-456", " 123456\n" all become 123456). A pasted code of
 * full length replaces the value; a shorter one is appended. Digits are set in the mono face (the number itself).
 */
export interface OtpInputProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Number of cells (6 for the TOTP and the e-mail/SMS one-time codes). */
  readonly length?: number;
  /** The input's accessible name ("Verification code"). */
  readonly label: string;
  readonly id?: string;
  readonly name?: string;
  readonly disabled?: boolean;
  /** Draws the cells with the danger border and sets `aria-invalid`. */
  readonly invalid?: boolean;
  readonly describedBy?: string;
  /** Called once, when typing or pasting fills the last cell. */
  readonly onComplete?: (value: string) => void;
  /** Marks the input as the initial focus of a modal layer (`data-autofocus`, read by `useModalFocus`). */
  readonly autoFocus?: boolean;
  /** Focuses the input when it mounts — for a field that is not inside a modal layer. */
  readonly focusOnMount?: boolean;
}

/** Keep only the digits and cut to `length`. Country-neutral: a code is digits, whatever the locale prints. */
export function normalizeOtp(input: string, length = 6): string {
  return input.replace(/\D/g, '').slice(0, length);
}

export function OtpInput({ value, onChange, length = 6, label, id, name, disabled = false, invalid = false, describedBy, onComplete, autoFocus = false, focusOnMount = false }: OtpInputProps): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const clean = normalizeOtp(value, length);
  const activeIndex = Math.min(clean.length, length - 1);

  useEffect(() => {
    if (focusOnMount) inputRef.current?.focus();
  }, [focusOnMount]);

  const commit = (next: string): void => {
    if (next === clean) return;
    onChange(next);
    if (next.length === length && clean.length < length) onComplete?.(next);
  };

  // The caret has no place in a code: keep it at the end so a tap on a cell never edits the middle.
  const toEnd = (): void => {
    const el = inputRef.current;
    if (el !== null) el.setSelectionRange(el.value.length, el.value.length);
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '');
    if (digits === '') return;
    commit(digits.length >= length ? digits.slice(0, length) : normalizeOtp(clean + digits, length));
  };

  return (
    <div className="vello-otp" data-invalid={invalid ? 'true' : undefined} data-disabled={disabled ? 'true' : undefined}>
      {Array.from({ length }, (_, i) => {
        const ch = clean[i];
        return (
          <span key={i} className="vello-otp__cell tnum" aria-hidden="true" data-filled={ch !== undefined ? 'true' : undefined} data-active={i === activeIndex ? 'true' : undefined}>
            {ch ?? <span className="vello-otp__caret" />}
          </span>
        );
      })}
      <input
        ref={inputRef}
        id={id}
        name={name}
        className="vello-otp__input"
        value={clean}
        onChange={(e) => commit(normalizeOtp(e.target.value, length))}
        onPaste={onPaste}
        onFocus={toEnd}
        onClick={toEnd}
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        aria-label={label}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={describedBy}
        disabled={disabled}
        spellCheck={false}
        data-autofocus={autoFocus ? '' : undefined}
      />
    </div>
  );
}
